import clsx from 'clsx'

import Icon, { IconColor, IconInversion, IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import InternalLink from '@/components/view/utils/link/InternalLink'

interface Props {
  type: IconType
  color?: TextColor
  iconColor?: IconColor
  link?: string
  disabled?: boolean
  line?: boolean
  text?: string
  weight?: 'normal' | 'bold'
  onClick?(e: React.MouseEvent<HTMLElement>): void
  behind?: boolean
  active?: boolean
  size?: 12 | 14 | 24
  iconInversion?: IconInversion
}

const IconButton = ({
  type,
  color = 'primary',
  iconColor,
  link,
  disabled = false,
  line = false,
  text,
  weight = 'normal',
  onClick,
  behind = false,
  active = false,
  size = 14,
  iconInversion
}: Props) => {
  const className = clsx(
    `group rounded-lg disabled:pointer-events-none inline-flex items-center gap-x-1 -my-0.5 outline-default focus-visible:outline-offset-0 w-fit min-w-[24px]`,
    !disabled && active && 'bg-fill-transparent-active',
    color == 'emphasis'
      ? 'bg-fill-tertiary p-2 hover:bg-fill-tertiary-hover active:bg-fill-tertiary-active'
      : 'hover:bg-fill-transparent-hover p-1 active:bg-fill-transparent-active'
  )
  const iconTag = (
    <Icon
      color={
        disabled
          ? IconColor.DISABLED
          : (iconColor ?? (color == 'secondary' ? IconColor.SECONDARY : IconColor.PRIMARY))
      }
      hoverColor={IconColor.HOVER}
      activeColor={IconColor.ACTIVE}
      type={type}
      line={line}
      inversion={iconInversion}
    />
  )
  const textTag = (
    <Text
      color={disabled ? TextColor.DISABLED : color}
      weight={weight}
      lineHeight={120}
      preWrap
      size={size}
    >
      {text}
    </Text>
  )
  return (
    <>
      {link && !disabled ? (
        <InternalLink link={link} className={className} tabIndex={disabled ? -1 : 0}>
          {!behind && iconTag}
          {text && textTag}
          {behind && iconTag}
        </InternalLink>
      ) : (
        <button type='button' className={className} onClick={onClick} disabled={disabled}>
          {!behind && iconTag}
          {text && textTag}
          {behind && iconTag}
        </button>
      )}
    </>
  )
}

export default IconButton
